<template>
    <template v-if="!quizAccess || !submitted">
        <meta-data page="login" />
        <div class="page">
            <div class="img">
                <i
                    class="fas fa-arrow-left"
                    @click="$router.push({ name: 'home' })"
                ></i>
            </div>
            <div class="form">
                <h1>{{ $translate('public.login.title') }}</h1>
                <h3>
                    {{ $translate('public.login.subtitle') }}
                </h3>
                <form @submit.prevent="login(form)">
                    <input
                        type="email"
                        :placeholder="$translate('public.login.form.email')"
                        v-model="form.email"
                        autocomplete="email"
                        required
                        autofocus
                        :class="{
                            inputIsInvalid:
                                authStore.$state.error == 'User does not exist',
                        }"
                    />
                    <input
                        type="password"
                        :placeholder="$translate('public.login.form.password')"
                        v-model="form.password"
                        autocomplete="current-password"
                        required
                        :class="{
                            inputIsInvalid:
                                authStore.$state.error == 'Wrong password',
                        }"
                    />
                    <p
                        v-if="authStore.$state.error == 'User does not exist'"
                        class="error-message"
                    >
                        {{ $translate('public.login.status.1') }}
                    </p>

                    <p
                        v-if="authStore.$state.error == 'User is not active'"
                        class="error-message"
                    >
                        {{ $translate('public.login.status.2') }}
                    </p>
                    <p
                        v-if="authStore.$state.error == 'Wrong password'"
                        class="error-message"
                    >
                        {{ $translate('public.login.status.3') }}
                    </p>

                    <p
                        v-if="authStore.$state.error == 'Too Many Attempts.'"
                        v-show="isTimerRunning"
                        class="error-message"
                    >
                        {{
                            $translate('public.login.status.4') +
                            ' ' +
                            countdown +
                            ' ' +
                            $translate('public.login.status.time')
                        }}
                    </p>
                    <primary-button
                        type="submit"
                        :disabled="
                            !form.password ||
                            !form.email ||
                            !form.email.includes('@') ||
                            isTimerRunning
                        "
                    >
                        {{ $translate('public.login.form.submit') }}
                    </primary-button>
                </form>
                <div class="links">
                    <a @click="$router.push({ name: 'new-access' })">
                        {{ $translate('public.login.form.new_access') }}
                    </a>

                    <a @click="$router.push({ name: 'forgot-password' })">
                        {{ $translate('public.login.form.forgot') }}
                    </a>
                </div>
            </div>
        </div>
    </template>
    <template v-else>
        <Quiz :email="form.email" />
    </template>
</template>
<script setup>
import { reactive, ref, watchEffect } from 'vue';

import { useApiStore } from '@/js/stores/apiStore';
import { useAuthStore } from '@/js/stores/authStore';
import { usePopupStore } from '@/js/stores/popupStore';

import ekeyRedirectDomains from '@/js/json/ekeyRedirectDomains.json';
import { useRoute, useRouter } from 'vue-router';

import Quiz from '@/js/views/public/auth/quiz/Index.vue';
import { useI18n } from 'vue-i18n';

const authStore = useAuthStore();
const apiStore = useApiStore();
const popupStore = usePopupStore();
const $router = useRouter();
const route = useRoute();

const { t } = useI18n();
const form = reactive({
    email: '',
    password: '',
});
const quizAccess = ref(false);
const submitted = ref(false);
const countdown = ref(60);
const isTimerRunning = ref(false);
let intervalId;

if (authStore.$state.isAuth) {
    $router.push({ name: 'dashboard' });
}

const getQuizAccess = async () => {
    await apiStore
        .apiGet('/api/quiz-access', { email: form.email })
        .then(res => {
            quizAccess.value = res.access;
        })
        .catch(() => {
            quizAccess.value = false;
        });
};

const checkEmailDomain = email => {
    return ekeyRedirectDomains.domains.some(domain =>
        email.toLowerCase().endsWith(domain),
    );
};

const login = async form => {
    if (checkEmailDomain(form.email)) {
        $router.push({ name: 'ekey-redirect' });
        return;
    }

    try {
        await getQuizAccess();
        submitted.value = true;
        if (!quizAccess.value) {
            await authStore.login(form, route.query.from);
        } else {
            popupStore.show('warning', t('public.quiz.login.warning'));
        }
    } catch (error) {
        popupStore.show('warning', t('public.login.error'));
    }
};
const timer = () => {
    isTimerRunning.value = true;
    intervalId = setInterval(() => {
        if (countdown.value > 0) {
            countdown.value--;
        } else {
            countdown.value = 60;
            clearInterval(intervalId);
            isTimerRunning.value = false;
        }
    }, 1000);
};

watchEffect(() => {
    if (authStore.$state.error === 'Too Many Attempts.') {
        timer();
    }
});
</script>
<style lang="scss" scoped>
@use '@/scss/variables' as vars;

.page {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100vh;

    .img {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-image: url('/storage/assets/images/login_image01.jpg');
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        width: 50%;
        height: 100%;

        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
                to left,
                vars.$whiteColor,
                rgba(0, 0, 0, 0)
            );
        }

        i {
            position: absolute;
            top: 2rem;
            left: 2rem;
            font-size: vars.$tertiaryFontSize;
            color: vars.$whiteColor;
            cursor: pointer;
        }
    }

    .form {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 50%;
        height: 100%;
        padding: 0 5rem;

        h3 {
            font-size: vars.$primaryFontSize;
            font-weight: 400;
            color: vars.$blackColor;
            margin-bottom: 2rem;
            text-align: center;
        }

        form {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
            margin-bottom: 2rem;
        }

        .error-message {
            font-size: vars.$defaultFontSize;
            color: vars.$redColor;
            margin-bottom: 1rem;
        }

        Button {
            margin-bottom: 1rem;
        }

        a {
            font-size: vars.$defaultFontSize;
            color: vars.$blackColor;
            text-decoration: none;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }
    }
}

.links {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 1rem;
}

@media (max-width: vars.$mediumBreakpoint) {
    .page {
        flex-direction: column;
        align-items: center;

        .img {
            width: 100%;
            height: 50%;

            &::before {
                background: linear-gradient(
                    to top,
                    vars.$whiteColor,
                    rgba(0, 0, 0, 0)
                );
            }
        }

        .form {
            z-index: 2;
            width: 100%;
            height: 50%;
            padding: 0 2rem;
        }
    }
}
</style>
